/**
 * Settings step — dashboard identity, theme, and refresh behaviour.
 */

import ImagePicker from '../components/settings/ImagePicker';
import { DEFAULT_LOGO_SIZE } from '../components/header/HeaderLogo';
import { REFRESH_OPTIONS, type DashboardConfig, type RefreshInterval } from './types/dashboardTypes';

interface Props {
  name: string;
  onNameChange: (name: string) => void;
  config: DashboardConfig;
  onChange: (config: DashboardConfig) => void;
}

const ACCENT_PRESETS = ['#2563EB', '#0D9488', '#7C3AED', '#DC2626', '#EA580C'];

const inputClass =
  'gl-input px-3 py-2 text-sm';

export default function DashboardSettings({ name, onNameChange, config, onChange }: Props) {
  return (
    <div className="max-w-xl space-y-5">
      <section className="space-y-3 gl-panel p-4">
        <h2 className="text-sm font-semibold text-slate-800">Dashboard</h2>

        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Name</span>
          <input
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
            className={`mt-1 ${inputClass}`}
          />
          <span className="mt-1 block text-[11px] text-slate-400">
            Shown on the application card and at the top of the dashboard.
          </span>
        </label>

        <label className="block">
          <span className="text-xs font-semibold text-slate-600">Description</span>
          <textarea
            value={config.description}
            onChange={(event) => onChange({ ...config, description: event.target.value })}
            rows={2}
            className={`mt-1 resize-none ${inputClass}`}
          />
        </label>
      </section>

      <section className="space-y-3 gl-panel p-4">
        <h2 className="text-sm font-semibold text-slate-800">Appearance</h2>

        <div>
          <span className="text-xs font-semibold text-slate-600">Accent colour</span>
          <p className="text-[11px] text-slate-400">
            Used by widgets that have no colour of their own.
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            {ACCENT_PRESETS.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => onChange({ ...config, theme: { ...config.theme, accentColor: color } })}
                aria-label={`Accent ${color}`}
                className={`h-7 w-7 rounded-full border-2 transition ${
                  config.theme.accentColor.toLowerCase() === color.toLowerCase()
                    ? 'scale-110 border-slate-800'
                    : 'border-transparent'
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
            <input
              type="color"
              value={config.theme.accentColor}
              onChange={(event) =>
                onChange({ ...config, theme: { ...config.theme, accentColor: event.target.value } })
              }
              aria-label="Custom accent colour"
              className="h-7 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
            />
          </div>
        </div>

        <div>
          <span className="text-xs font-semibold text-slate-600">Divider colour</span>
          <p className="text-[11px] text-slate-400">
            The border around every widget's box, set once for the whole dashboard. A widget's own
            outline colour (set on that widget) still wins over this.
          </p>
          <div className="mt-1.5 flex items-center gap-2">
            <input
              type="color"
              value={config.theme.dividerColor ?? '#334155'}
              onChange={(event) =>
                onChange({ ...config, theme: { ...config.theme, dividerColor: event.target.value } })
              }
              aria-label="Divider colour"
              className="h-7 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
            />
            {config.theme.dividerColor && (
              <button
                type="button"
                onClick={() =>
                  onChange({ ...config, theme: { ...config.theme, dividerColor: undefined } })
                }
                className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Dashboards are often shown on a wall display, where a dark board
            reads far better than a white one — independently of whatever theme
            the person who built it happens to be using. */}
        <div>
          <span className="text-xs font-semibold text-slate-600">Dashboard theme</span>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            {([
              { id: 'light' as const, label: 'Follow application' },
              { id: 'dark' as const, label: 'Always dark' },
            ]).map((option) => (
              <button
                key={option.id}
                type="button"
                aria-pressed={config.theme.mode === option.id}
                onClick={() =>
                  onChange({ ...config, theme: { ...config.theme, mode: option.id } })
                }
                className={`rounded-lg border px-3 py-2 text-xs font-medium transition ${
                  config.theme.mode === option.id
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-slate-400">
            &ldquo;Always dark&rdquo; keeps this dashboard dark even when the application is in
            light mode.
          </p>
        </div>

        <div className="space-y-3 border-t border-slate-100 pt-3">
          <ImagePicker
            label="Logo"
            value={config.theme.logoUrl}
            onChange={(logoUrl) =>
              onChange({ ...config, theme: { ...config.theme, logoUrl } })
            }
            hint="Shown in this dashboard's header, on the side set below. Leave empty for the built-in compass mark."
            maxDimension={512}
            targetBytes={120 * 1024}
          />

          <label className="block">
            <span className="text-xs font-semibold text-slate-600">
              Logo height
              <span className="ml-1.5 font-normal tabular-nums text-slate-400">
                {config.theme.logoSize ?? DEFAULT_LOGO_SIZE} px
              </span>
            </span>
            <input
              type="range"
              min={18}
              max={48}
              value={config.theme.logoSize ?? DEFAULT_LOGO_SIZE}
              onChange={(event) =>
                onChange({
                  ...config,
                  theme: { ...config.theme, logoSize: Number(event.target.value) },
                })
              }
              className="mt-1.5 w-full"
            />
          </label>

          <div>
            <span className="text-xs font-semibold text-slate-600">Logo position</span>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {([
                { id: 'left' as const, label: 'Left' },
                { id: 'right' as const, label: 'Right' },
              ]).map((option) => (
                <button
                  key={option.id}
                  type="button"
                  aria-pressed={(config.theme.logoPosition ?? 'left') === option.id}
                  onClick={() =>
                    onChange({ ...config, theme: { ...config.theme, logoPosition: option.id } })
                  }
                  className={`rounded-lg border px-3 py-2 text-xs font-medium transition ${
                    (config.theme.logoPosition ?? 'left') === option.id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-3 border-t border-slate-100 pt-3">
          <h3 className="text-xs font-semibold text-slate-600">Header style</h3>

          <div>
            <span className="text-xs font-semibold text-slate-600">Background colour</span>
            <p className="text-[11px] text-slate-400">Leave unset to keep the header's own glass tint.</p>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="color"
                value={config.theme.headerBgColor ?? '#0F2942'}
                onChange={(event) =>
                  onChange({ ...config, theme: { ...config.theme, headerBgColor: event.target.value } })
                }
                aria-label="Header background colour"
                className="h-7 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
              />
              {config.theme.headerBgColor && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({ ...config, theme: { ...config.theme, headerBgColor: undefined } })
                  }
                  className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          <div>
            <span className="text-xs font-semibold text-slate-600">Title colour</span>
            <p className="text-[11px] text-slate-400">
              Set this alongside a background colour above — the default title colour may stop
              reading against a custom background.
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              <input
                type="color"
                value={config.theme.headerTextColor ?? '#0F172A'}
                onChange={(event) =>
                  onChange({ ...config, theme: { ...config.theme, headerTextColor: event.target.value } })
                }
                aria-label="Header title colour"
                className="h-7 w-12 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
              />
              {config.theme.headerTextColor && (
                <button
                  type="button"
                  onClick={() =>
                    onChange({ ...config, theme: { ...config.theme, headerTextColor: undefined } })
                  }
                  className="text-[11px] font-medium text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-2 gl-panel p-4">
        <h2 className="text-sm font-semibold text-slate-800">Data refresh</h2>
        <p className="text-[11px] text-slate-400">
          How often published widgets re-query their services. Manual is the default —
          automatic refresh puts continuous load on the GIS services.
        </p>
        <select
          value={config.refreshInterval}
          onChange={(event) =>
            onChange({ ...config, refreshInterval: Number(event.target.value) as RefreshInterval })
          }
          aria-label="Refresh interval"
          className={inputClass}
        >
          {REFRESH_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </section>

      <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          About this dashboard
        </h2>
        <dl className="mt-2 space-y-1 text-[11px] text-slate-500">
          <div className="flex justify-between gap-3">
            <dt>Data sources</dt>
            <dd className="font-medium text-slate-700">{config.dataSources.length}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Widgets</dt>
            <dd className="font-medium text-slate-700">{config.widgets.length}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Schema version</dt>
            <dd className="font-medium text-slate-700">{config.schemaVersion}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Last updated</dt>
            <dd className="font-medium text-slate-700">
              {new Date(config.updatedAt).toLocaleString()}
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
